import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Clock, Edit3, Filter, Search, X, XCircle } from "lucide-react";
import "../styles/admin-base.css";
import "../styles/approval.css";
import { mockProponentTasks } from "../components/ai/mockAiData";
import ProponentCard from "../components/ai/ProponentCard";
import ConfidenceBar from "../components/ai/ConfidenceBar";

const STATUS_FILTER_OPTIONS = [
  "All Statuses",
  "Pending",
  "Approved",
  "Rejected",
  "In Edit",
];

const CATEGORY_FILTER_OPTIONS = [
  "All Categories",
  "Medical Assistance",
  "Legal Consultation",
  "Educational Scholarship",
];

const PROPONENT_FILTER_OPTIONS = [
  "All Proponent",
  "Dr. Ana Rodriguez",
  "Atty. Carlos Mendoza",
];

const normalizeStatus = (status) => {
  if (status === "approved" || status === "rejected" || status === "editing") {
    return status;
  }
  return "pending";
};

const buildQueueStats = (tasks) => {
  const summary = {
    total: tasks.length,
    pending: 0,
    approved: 0,
    editing: 0,
    rejected: 0,
    overdue: 0,
  };

  const now = Date.now();

  tasks.forEach((task) => {
    const status = normalizeStatus(task.status);
    summary[status] += 1;

    const due = new Date(task.dueDate).getTime();
    if (Number.isFinite(due)) {
      if (due < now) {
        summary.overdue += 1;
      }
    }
  });

  return summary;
};

const QUEUE_METRIC_CONFIG = [
  { id: "pending", label: "Pending Review", icon: Clock, tone: "is-blue" },
  { id: "approved", label: "Approved Today", icon: CheckCircle2, tone: "is-green" },
  { id: "editing", label: "In Edit", icon: Edit3, tone: "is-purple" },
  { id: "rejected", label: "Rejected", icon: XCircle, tone: "is-red" },
  { id: "overdue", label: "Overdue", icon: AlertTriangle, tone: "is-amber" },
];

const ProponentQueueMetrics = ({ stats }) => (
  <div className="proponent-queue__metrics">
    {QUEUE_METRIC_CONFIG.map(({ id, label, icon: Icon, tone }) => (
      <article key={id} className={`admin-card admin-card--center ${tone}`.trim()}>
        <Icon size={18} />
        <div className="admin-card__label">{label}</div>
        <div className="admin-card__value">{stats[id]}</div>
      </article>
    ))}
  </div>
);

ProponentQueueMetrics.propTypes = {
  stats: PropTypes.shape({
    pending: PropTypes.number.isRequired,
    approved: PropTypes.number.isRequired,
    editing: PropTypes.number.isRequired,
    rejected: PropTypes.number.isRequired,
    overdue: PropTypes.number.isRequired,
  }).isRequired,
};

const ProponentQueueFilters = () => (
  <aside className="admin-surface proponent-queue__filters">
    <div className="proponent-queue__filters-header">
      <Filter size={18} aria-hidden="true" />
      <h3>Filters</h3>
    </div>
    <div className="proponent-queue__filters-body">
      <label className="admin-field">
        <span>Search</span>
        <div className="proponent-queue__input">
          <Search size={16} />
          <input type="text" placeholder="Ticket ID, member, category…" disabled />
        </div>
      </label>
      <label className="admin-field">
        <span>Status</span>
        <select defaultValue="All Statuses" disabled>
          {STATUS_FILTER_OPTIONS.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </label>
      <label className="admin-field">
        <span>Category</span>
        <select defaultValue="All Categories" disabled>
          {CATEGORY_FILTER_OPTIONS.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </label>
      <label className="admin-field">
        <span>Proponent</span>
        <select defaultValue="All Proponent" disabled>
          {PROPONENT_FILTER_OPTIONS.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </label>
    </div>
  </aside>
);

const ProponentQueueList = ({ tasks, onEdit, onApprove, onReject }) => (
  <div className="proponent-queue__list">
    {tasks.length === 0 ? (
      <div className="admin-card admin-empty-state">No items in the proponent queue.</div>
    ) : (
      tasks.map((task) => (
        <ProponentCard
          key={task.id}
          proponent={task.proponent}
          ticketId={task.ticketId}
          memberPseudonym={task.memberPseudonym}
          suggestedResponse={task.suggestedResponse}
          dueDate={task.dueDate}
          status={normalizeStatus(task.status)}
          confidence={task.aiConfidence}
          onEdit={onEdit ? () => onEdit(task) : undefined}
          onApprove={onApprove ? () => onApprove(task.id) : undefined}
          onReject={onReject ? () => onReject(task.id) : undefined}
        />
      ))
    )}
  </div>
);

ProponentQueueList.propTypes = {
  tasks: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
  onEdit: PropTypes.func,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
};

const ProponentQueueView = ({ tasks, onApprove, onReject }) => {
  const stats = useMemo(() => buildQueueStats(tasks), [tasks]);
  const overdueCount = stats.overdue;
  const [editingTask, setEditingTask] = useState(null);
  const [responseDraft, setResponseDraft] = useState("");
  const [responseOverrides, setResponseOverrides] = useState({});

  const tasksWithOverrides = useMemo(
    () => tasks.map((task) => (
      responseOverrides[task.id]
        ? { ...task, suggestedResponse: responseOverrides[task.id] }
        : task
    )),
    [tasks, responseOverrides],
  );

  const handleOpenEdit = (task) => {
    const currentText = responseOverrides[task.id] ?? task.suggestedResponse;
    setEditingTask(task);
    setResponseDraft(currentText);
  };

  const handleCloseEdit = () => {
    setEditingTask(null);
    setResponseDraft("");
  };

  const handleSubmitEdit = () => {
    if (!editingTask) return;
    const originalTask = tasks.find((taskItem) => taskItem.id === editingTask.id);
    const fallbackText = originalTask?.suggestedResponse ?? editingTask.suggestedResponse;
    setResponseOverrides((prev) => ({
      ...prev,
      [editingTask.id]: responseDraft.trim() === "" ? fallbackText : responseDraft,
    }));
    handleCloseEdit();
  };

  return (
    <section className="proponent-queue admin-stack-lg">
      <ProponentQueueMetrics stats={stats} />

      <div className="proponent-queue__layout">
        <ProponentQueueFilters />
        <div className="proponent-queue__content">
          <header className="proponent-queue__list-header">
            <h2>Proposal Queue</h2>
            <span className="admin-muted">{`${tasksWithOverrides.length} ${tasksWithOverrides.length === 1 ? "proposal" : "proposals"}`}</span>
          </header>
          {overdueCount > 0 ? (
            <div className="proponent-queue__banner">
              <AlertTriangle size={16} />
              <span>
                {overdueCount === 1
                  ? "1 overdue proposal requires immediate attention"
                  : `${overdueCount} overdue proposals require immediate attention`}
              </span>
            </div>
          ) : null}

          <ProponentQueueList
            tasks={tasksWithOverrides}
            onEdit={handleOpenEdit}
            onApprove={onApprove}
            onReject={onReject}
          />
        </div>
      </div>

      {editingTask ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={handleCloseEdit} />
          <div className="admin-dialog__panel admin-stack-lg" role="document">
            <header className="admin-dialog__header">
              <div>
                <h3>Edit Response - {editingTask.ticketId}</h3>
                <p className="admin-muted">Update the AI-suggested response before sending to final admin approval.</p>
              </div>
              <button type="button" className="admin-button admin-button--ghost" onClick={handleCloseEdit}>
                <X size={16} />
              </button>
            </header>

            <div className="admin-dialog__body admin-stack-md">
              <div className="admin-grid-two">
                <div>
                  <span className="admin-muted">Member</span>
                  <p>{editingTask.memberPseudonym}</p>
                </div>
                <div>
                  <span className="admin-muted">Category</span>
                  <p>{editingTask.category || "Medical Assistance"}</p>
                </div>
                <div>
                  <span className="admin-muted">Proponent</span>
                  <p>{editingTask.proponent?.name}</p>
                </div>
                <div>
                  <span className="admin-muted">Due Date</span>
                  <p>{new Date(editingTask.dueDate).toLocaleString()}</p>
                </div>
              </div>

              {typeof editingTask.aiConfidence === "number" ? (
                <div className="admin-stack-sm">
                  <span className="admin-muted">AI Confidence</span>
                  <div className="admin-row">
                    <strong>{Math.round(editingTask.aiConfidence * 100)}%</strong>
                    <ConfidenceBar confidence={editingTask.aiConfidence} size="md" showNumeric={false} />
                  </div>
                </div>
              ) : null}

              <label className="admin-field">
                <span>Response Text</span>
                <textarea
                  rows={6}
                  value={responseDraft}
                  onChange={(event) => setResponseDraft(event.target.value)}
                />
              </label>

              <p className="admin-muted">This will be sent to Admin for final approval.</p>
            </div>

            <footer className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--ghost" onClick={handleCloseEdit}>
                Cancel
              </button>
              <button type="button" className="admin-button is-primary" onClick={handleSubmitEdit}>
                Send to Admin for final approval
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </section>
  );
};

ProponentQueueView.propTypes = {
  tasks: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
};

export function ProponentQueueSection({ tasks = mockProponentTasks }) {
  const [localTasks, setLocalTasks] = useState(tasks);

  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  const handleApprove = (taskId) => setLocalTasks((prev) => prev.map((task) => (
    task.id === taskId ? { ...task, status: "approved" } : task
  )));

  const handleReject = (taskId) => setLocalTasks((prev) => prev.map((task) => (
    task.id === taskId ? { ...task, status: "rejected" } : task
  )));

  return (
    <ProponentQueueView
      tasks={localTasks}
      onApprove={handleApprove}
      onReject={handleReject}
    />
  );
}

ProponentQueueSection.propTypes = {
  tasks: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
};

export default function ProponentQueue() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(mockProponentTasks);

  const handleApprove = (taskId) => setTasks((prev) => prev.map((task) => (
    task.id === taskId ? { ...task, status: "approved" } : task
  )));

  const handleReject = (taskId) => setTasks((prev) => prev.map((task) => (
    task.id === taskId ? { ...task, status: "rejected" } : task
  )));

  return (
    <div className="admin-page admin-stack-lg proponent-page proponent-page--queue">
      <header className="admin-row proponent-queue__header">
        <div className="proponent-queue__header-left">
          <button
            type="button"
            className="admin-button admin-button--ghost"
            onClick={() => navigate("/admin/proponent-assistance")}
          >
            <ArrowLeft size={16} /> Back to Benefits Overview
          </button>
          <div>
            <h1>Proponent Queue</h1>
            <p className="admin-muted">Review AI-generated proposal drafts before final admin approvals.</p>
          </div>
        </div>
        <span className="proponent-queue__header-count">
          {`${tasks.length} ${tasks.length === 1 ? "proposal" : "proposals"}`}
        </span>
      </header>

      <ProponentQueueView tasks={tasks} onApprove={handleApprove} onReject={handleReject} />

      <p className="admin-muted proponent-queue__note">Prototype view only — actions are disabled and data is static.</p>
    </div>
  );
}
